(function (global) {
  'use strict';

  const TARGET_THEME = 186470301969;
  const TARGET_IMAGE = 'https://cdn.flashyapp.com/rq08jl/PL8hu8xd.png';
  const GAP = 8;
  const ART_BOTTOM_GAP = 4;
  const EDGE = 12;

  function solveGeometry(input) {
    const { width, height, emailTop, contentBottom, paddingTop, paddingBottom } = input;
    if (![width, height, emailTop, contentBottom, paddingTop, paddingBottom].every(Number.isFinite) ||
        width <= 0 || height <= 0 || contentBottom < emailTop || paddingTop < 0 || paddingBottom < 0) {
      return { ok: false, reason: 'invalid-geometry' };
    }
    const scale = Math.min(width / 400, height / 800);
    const textBottom = 541 * scale;
    const transfer = Math.ceil(Math.max(0, textBottom + GAP - emailTop) * 100) / 100;
    if (transfer > paddingBottom || contentBottom + transfer > 800 * scale - ART_BOTTOM_GAP) {
      return { ok: false, reason: 'insufficient-bottom-space', transfer, textBottom };
    }
    return {
      ok: true,
      transfer,
      textBottom,
      imageWidth: 400 * scale,
      imageHeight: 800 * scale,
      paddingTop: paddingTop + transfer,
      paddingBottom: paddingBottom - transfer,
      expectedGap: emailTop + transfer - textBottom
    };
  }

  function authorizedTheme(theme) {
    const contract = typeof module === 'object' && module.exports ? require('./sw-next-release-contract.js') : global.SwNextReleaseContract;
    return Boolean(contract && contract.enabled('newsletter-overlap', theme));
  }

  function closeTranslation(origin, box, scrollTop) {
    const top = Math.max(EDGE, Math.min(origin.top, box.height - origin.height - EDGE));
    const left = Math.max(EDGE, Math.min(origin.left, box.width - origin.width - EDGE));
    return { x: left - origin.left, y: scrollTop + top - origin.top };
  }

  function matchesBackground(css) {
    const match = /^url\(["']?([^"')]+)["']?\)$/.exec(css.backgroundImage);
    return !!match && match[1] === TARGET_IMAGE && css.backgroundSize === 'contain' &&
      css.backgroundPosition === '50% 0%' && css.backgroundOrigin === 'padding-box' &&
      css.backgroundRepeat === 'no-repeat';
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { solveGeometry, authorizedTheme, matchesBackground, closeTranslation };
    return;
  }
  if (!authorizedTheme(global.Shopify && global.Shopify.theme) ||
      global.__swNewsletterOverlapNext20261027) return;
  global.__swNewsletterOverlapNext20261027 = true;

  const document = global.document;
  const states = new WeakMap();
  const px = value => Number.parseFloat(value) || 0;
  const visible = element => element.getClientRects().length > 0;
  const normalText = element => (element.textContent || '').replace(/\s+/g, ' ').trim();

  function viewport() {
    const view = global.visualViewport;
    return view ? { width: view.width, height: view.height, left: view.offsetLeft, top: view.offsetTop } :
      { width: global.innerWidth, height: global.innerHeight, left: 0, top: 0 };
  }

  function saveProperties(element, names) {
    return { element, properties: names.map(name => ({ name,
      value: element.style.getPropertyValue(name), priority: element.style.getPropertyPriority(name) })) };
  }

  function restore(state) {
    state.saved.forEach(({ element, properties }) => properties.forEach(({ name, value, priority }) => {
      if (value) element.style.setProperty(name, value, priority);
      else element.style.removeProperty(name);
    }));
  }

  function put(element, name, value) {
    element.style.setProperty(name, value, 'important');
  }

  function observeForm(state) {
    if (!state.observer) return;
    const rect = state.form.getBoundingClientRect();
    const popupRect = state.popup.getBoundingClientRect();
    state.observedBox = rect.width + ':' + rect.height + ':' + popupRect.width + ':' + popupRect.height;
    state.observer.observe(state.form);
    state.observer.observe(state.popup, { box: 'border-box' });
  }

  function fingerprint(host) {
    if (!host || host.localName !== 'flashy-popup' || !host.isConnected || !host.shadowRoot) return null;
    const root = host.shadowRoot;
    const popups = Array.from(root.querySelectorAll('.fls-popup')).filter(visible);
    if (popups.length !== 1) return null;
    const popup = popups[0];
    const forms = Array.from(popup.querySelectorAll('form.fls-popup-form')).filter(visible);
    if (forms.length !== 1) return null;
    const form = forms[0];
    if (!popup.contains(form) || !matchesBackground(global.getComputedStyle(popup))) return null;
    const emails = Array.from(form.querySelectorAll('input[type="email"],input[name="email"]')).filter(visible);
    const fields = Array.from(form.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"])')).filter(visible);
    const checks = Array.from(form.querySelectorAll('input[type="checkbox"]')).filter(visible);
    const buttons = Array.from(form.querySelectorAll('button,input[type="submit"]')).filter(visible);
    if (emails.length !== 1 || fields.length !== 2 || checks.length !== 1 ||
        buttons.length !== 1 || normalText(buttons[0]) !== 'אני בפנים') return null;
    const columns = form.closest('.fls-columns');
    const closes = Array.from(popup.querySelectorAll('.fls-close-popup')).filter(visible);
    if (closes.length !== 1) return null;
    const close = closes[0];
    const closeHits = Array.from(close.querySelectorAll('.fls-close')).filter(visible);
    const closeHit = closeHits.length === 1 && closeHits[0];
    const dialog = popup.closest('dialog');
    if (!columns || !popup.contains(columns) || !columns.contains(form) || !closeHit || !dialog || dialog.getRootNode() !== root) return null;
    return { host, root, popup, form, email: emails[0], checkbox: checks[0], button: buttons[0], columns, close, closeHit, dialog };
  }

  function fit(state) {
    if (!state.host.isConnected || !authorizedTheme(global.Shopify && global.Shopify.theme)) {
      state.dispose();
      return;
    }
    state.observer && state.observer.disconnect();
    state.clipped = false;
    const previousScroll = state.popup.scrollTop + state.columns.scrollTop;
    restore(state);
    const target = fingerprint(state.host);
    if (!target || target.form !== state.form) {
      state.dispose();
      if (target) attach(state.host);
      return;
    }
    const view = viewport();
    const availableWidth = view.width - EDGE * 2;
    const availableHeight = view.height - EDGE * 2;
    let css = global.getComputedStyle(state.popup);
    let rect = state.popup.getBoundingClientRect();
    const horizontalExtra = px(css.paddingLeft) + px(css.paddingRight) + px(css.borderLeftWidth) + px(css.borderRightWidth);
    const verticalExtra = px(css.paddingTop) + px(css.paddingBottom) + px(css.borderTopWidth) + px(css.borderBottomWidth);
    const fail = reason => {
      state.clipped = false;
      restore(state);
      state.host.dataset.swNewsletterLayout = reason;
      observeForm(state);
    };
    if (availableWidth < 200 || availableHeight < 160 ||
        px(css.borderTopWidth) || px(css.borderBottomWidth) ||
        px(css.borderLeftWidth) || px(css.borderRightWidth)) {
      fail('unsupported-viewport-or-border');
      return;
    }
    if (rect.width > availableWidth) {
      put(state.popup, 'min-width', '0');
      put(state.popup, 'max-width', (availableWidth - (css.boxSizing === 'content-box' ? horizontalExtra : 0)) + 'px');
      rect = state.popup.getBoundingClientRect();
    }
    const formCss = global.getComputedStyle(state.form);
    const emailTop = state.email.getBoundingClientRect().top - rect.top + state.popup.scrollTop + state.columns.scrollTop;
    const contentBottom = Math.max(state.button.getBoundingClientRect().bottom, state.checkbox.getBoundingClientRect().bottom) -
      rect.top + state.popup.scrollTop + state.columns.scrollTop;
    const solution = solveGeometry({ width: rect.width, height: rect.height, emailTop, contentBottom,
      paddingTop: px(formCss.paddingTop), paddingBottom: px(formCss.paddingBottom) });
    if (!solution.ok) {
      fail(solution.reason);
      return;
    }
    put(state.form, 'padding-top', solution.paddingTop + 'px');
    put(state.form, 'padding-bottom', solution.paddingBottom + 'px');
    const after = state.popup.getBoundingClientRect();
    if (Math.abs(after.height - rect.height) > 0.75 || Math.abs(after.width - rect.width) > 0.75) {
      fail('padding-transfer-changed-image-size');
      return;
    }
    const clipped = after.height > availableHeight || state.columns.scrollHeight > state.columns.clientHeight + 1;
    if (clipped) {
      const closeCss = global.getComputedStyle(state.close);
      if (closeCss.position !== 'absolute' || !['none', '0px', '0px 0px'].includes(closeCss.translate)) {
        fail('unsupported-close-position');
        return;
      }
      const hit = state.closeHit.getBoundingClientRect();
      state.closeOrigin = { left: hit.left - after.left, top: hit.top - after.top + state.popup.scrollTop,
        width: hit.width, height: hit.height };
      put(state.popup, 'background-size', solution.imageWidth + 'px ' + solution.imageHeight + 'px');
      put(state.popup, 'background-attachment', 'local');
      put(state.popup, 'min-height', '0');
      put(state.popup, 'max-height', (availableHeight - (css.boxSizing === 'content-box' ? verticalExtra : 0)) + 'px');
      put(state.popup, 'overflow-y', 'auto');
      put(state.popup, 'overflow-x', 'hidden');
      put(state.popup, 'overscroll-behavior', 'contain');
      put(state.columns, 'height', 'auto');
      put(state.columns, 'max-height', 'none');
      put(state.columns, 'overflow', 'visible');
      put(state.form, 'flex-shrink', '0');
      state.clipped = true;
    }
    put(state.popup, 'top', (view.top + view.height / 2) + 'px');
    put(state.popup, 'left', (view.left + view.width / 2) + 'px');
    put(state.popup, 'bottom', 'auto');
    put(state.popup, 'right', 'auto');
    put(state.popup, 'transform', 'translate(-50%, -50%)');
    state.popup.scrollTop = previousScroll;
    const finalRect = state.popup.getBoundingClientRect();
    const finalEmailTop = state.email.getBoundingClientRect().top - finalRect.top + state.popup.scrollTop;
    const gap = finalEmailTop - solution.textBottom;
    const lastBottom = Math.max(state.button.getBoundingClientRect().bottom, state.checkbox.getBoundingClientRect().bottom);
    const finalContentBottom = lastBottom - finalRect.top + state.popup.scrollTop;
    if (gap < GAP - 0.25 || finalRect.height > availableHeight + 1 ||
        finalContentBottom > Math.max(state.popup.clientHeight, state.popup.scrollHeight) + 1 ||
        finalContentBottom > solution.imageHeight - ART_BOTTOM_GAP + 0.25) {
      fail('post-layout-safety-check-failed');
      return;
    }
    const active = state.root.activeElement;
    if (clipped && active && state.form.contains(active)) {
      const activeRect = active.getBoundingClientRect();
      if (activeRect.bottom > finalRect.bottom - EDGE) state.popup.scrollTop += activeRect.bottom - finalRect.bottom + EDGE;
      else if (activeRect.top < finalRect.top + EDGE) state.popup.scrollTop -= finalRect.top + EDGE - activeRect.top;
    }
    if (clipped) {
      state.pinClose();
      const hit = state.closeHit.getBoundingClientRect();
      if (hit.top < finalRect.top || hit.bottom > finalRect.bottom || hit.left < finalRect.left || hit.right > finalRect.right) {
        state.clipped = false;
        fail('close-control-outside-viewport');
        return;
      }
    }
    state.host.dataset.swNewsletterLayout = 'candidate-applied-not-qa';
    state.host.dataset.swNewsletterGap = gap.toFixed(2);
    observeForm(state);
  }

  function attach(host) {
    if (states.has(host)) return;
    const target = fingerprint(host);
    if (!target) return;
    const state = { ...target, raf: 0 };
    state.saved = [
      saveProperties(state.form, ['padding-top', 'padding-bottom', 'flex-shrink']),
      saveProperties(state.popup, ['min-width', 'max-width', 'background-size', 'background-attachment', 'min-height',
        'max-height', 'overflow-y', 'overflow-x', 'overscroll-behavior', 'top', 'left', 'bottom', 'right', 'transform']),
      saveProperties(state.columns, ['height', 'max-height', 'overflow']),
      saveProperties(state.close, ['translate', 'z-index'])
    ];
    state.pinClose = () => {
      if (!state.clipped || !state.host.isConnected) return;
      const shift = closeTranslation(state.closeOrigin,
        { width: state.popup.clientWidth, height: state.popup.clientHeight }, state.popup.scrollTop);
      put(state.close, 'translate', shift.x + 'px ' + shift.y + 'px');
      put(state.close, 'z-index', '2147483647');
    };
    const schedule = () => {
      if (!state.raf) state.raf = global.requestAnimationFrame(() => { state.raf = 0; fit(state); });
    };
    const animationSettled = event => {
      if (event.target && event.target.matches && event.target.matches('.fls-animate')) schedule();
    };
    const close = () => { if (!host.isConnected) state.dispose(); };
    const dialogClosed = () => state.dispose();
    state.dispose = () => {
      state.clipped = false;
      if (state.raf) global.cancelAnimationFrame(state.raf);
      state.observer && state.observer.disconnect();
      global.removeEventListener('resize', schedule);
      global.removeEventListener('flashyPopupClosed', close);
      state.dialog.removeEventListener('close', dialogClosed);
      state.root.removeEventListener('animationend', animationSettled, true);
      state.root.removeEventListener('animationcancel', animationSettled, true);
      state.popup.removeEventListener('scroll', state.pinClose);
      global.visualViewport && global.visualViewport.removeEventListener('resize', schedule);
      global.visualViewport && global.visualViewport.removeEventListener('scroll', schedule);
      document.fonts && document.fonts.removeEventListener('loadingdone', schedule);
      restore(state);
      delete host.dataset.swNewsletterLayout;
      delete host.dataset.swNewsletterGap;
      states.delete(host);
    };
    if (typeof global.ResizeObserver === 'function') state.observer = new global.ResizeObserver(() => {
      const rect = state.form.getBoundingClientRect();
      const popupRect = state.popup.getBoundingClientRect();
      if (rect.width + ':' + rect.height + ':' + popupRect.width + ':' + popupRect.height !== state.observedBox) schedule();
    });
    states.set(host, state);
    global.addEventListener('resize', schedule, { passive: true });
    global.addEventListener('flashyPopupClosed', close);
    state.dialog.addEventListener('close', dialogClosed);
    state.root.addEventListener('animationend', animationSettled, true);
    state.root.addEventListener('animationcancel', animationSettled, true);
    state.popup.addEventListener('scroll', state.pinClose, { passive: true });
    global.visualViewport && global.visualViewport.addEventListener('resize', schedule, { passive: true });
    global.visualViewport && global.visualViewport.addEventListener('scroll', schedule, { passive: true });
    document.fonts && document.fonts.addEventListener('loadingdone', schedule);
    schedule();
  }

  global.addEventListener('flashyPopupShowed', event => attach(event.detail && event.detail.root));
  const scanExisting = () => document.querySelectorAll('flashy-popup').forEach(attach);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scanExisting, { once: true });
  else scanExisting();
})(typeof window === 'undefined' ? null : window);
